import {
  Table,
  DatePicker,
  Select,
  Pagination,
  Dropdown,
  Menu,
  Modal,
} from "antd";
import type { PaginationProps } from "antd";
import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import type { RangePickerProps } from "antd/es/date-picker";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { InvoiceStatus } from "../../shared/constant";
import tableUtils from "../../utils/tableUtils";
import {
  updateInvoice,
  resetInvoices,
  retrieveInvoices,
  resetInvoiceSent,
} from "../../redux/actions/invoiceActions";
import { RootState } from "../../redux/rootReducer";
import retrieveCurrencies from "../../redux/actions/currencyActions";
import { Currency } from "../../types/Currency/Currency";
import globalUtils from "../../utils/globalUtils";
import ROUTES from "../../routes";
import {
  ClientInvoicesColumn,
  InvoicesColumn,
} from "../../shared/table/columns";
import icons from "../../shared/icons";
import { resetPayments } from "../../redux/actions/paymentActions";
import SearchComponent from "./SearchComponent";
import { getAllClients } from "../../redux/actions/accountActions";
import { isClapAdminORClapManager } from "../../utils/clapUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;

function ClapInvoices({ isClientDetails }: { isClientDetails?: Boolean }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { clientId } = useParams();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [currency, setCurrency] = useState("");
  const [client, setClient] = useState("");
  const [status, setStatus] = useState("");
  const [effect, setEffect] = useState(false);
  // const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { invoices, invoicesResponse, updateInvoiceSuccess, invoicesPending } =
    useSelector((state: RootState) => state.invoice);
  const { currencies } = useSelector((state: RootState) => state.currency);
  const [columns, setColumns] = useState<any[]>([]);
  const { userDetails } = useSelector((state: RootState) => state.user);
  const { allClients } = useSelector((state: RootState) => state.account);

  useEffect(() => {
    if (isClientDetails) {
      setColumns(ClientInvoicesColumn);
      return;
    }
    dispatch(getAllClients());
    setColumns(InvoicesColumn);
  }, []);

  useEffect(() => {
    dispatch(resetInvoices());
    dispatch(resetPayments());
    dispatch(retrieveCurrencies());
  }, []);

  useEffect(() => {
    dispatch(
      retrieveInvoices({
        ...(isClientDetails ? { clientId } : { clientId: client }),
        page,
        searchText,
        dateRange,
        currency,
        status,
      }),
    );
  }, [page, effect]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
    setEffect(!effect);
  };

  const onRangeChange: RangePickerProps["onChange"] = (dates, dateStrings) => {
    if (dates) setDateRange({ from: dateStrings[0], to: dateStrings[1] });
    else setDateRange({ from: "", to: "" });
    setPage(1);
    setEffect(!effect);
  };

  const onCurrencyChange = (value: string) => {
    if (value) setCurrency(value);
    else setCurrency("");
    setPage(1);
    setEffect(!effect);
  };

  const onStatusChange = (value: string) => {
    if (value) setStatus(value);
    else setStatus("");
    setPage(1);
    setEffect(!effect);
  };

  const onClientChange = (value: string) => {
    if (value) setClient(value);
    else setClient("");
    setPage(1);
    setEffect(!effect);
  };

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  // const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
  //   console.log("selectedRowKeys changed: ", selectedRowKeys);
  //   console.log(newSelectedRowKeys);
  //   setSelectedRowKeys(newSelectedRowKeys);
  // };

  // const rowSelection = {
  //   selectedRowKeys,
  //   onChange: onSelectChange,
  // };

  useEffect(() => {
    if (updateInvoiceSuccess) {
      dispatch(resetInvoiceSent());
      setEffect(!effect);
    }
  }, [updateInvoiceSuccess]);

  const onUpdateInvoiceStatus = (record: any, invoiceStatus: string) => {
    Modal.confirm({
      title: `Are you sure to mark this invoice as ${globalUtils.snakeCaseToCapitalize(
        invoiceStatus,
      )}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        await dispatch(updateInvoice({ id: record.id, status: invoiceStatus }));
      },
      onCancel() {},
    });
  };

  const actions = {
    title: "",
    key: "action",
    align: "right",
    className: "cursor-default",
    onCell: () => {
      return {
        onClick: (e: any) => {
          e.stopPropagation();
        },
      };
    },
    render: (_: any, record: any) =>
      isClapAdminORClapManager(userDetails) &&
      [InvoiceStatus.DRAFT, InvoiceStatus.INVOICE_SENT].includes(
        record.status,
      ) && (
        <div className="text-secondary">
          <Dropdown
            overlay={
              <Menu
                className="min-w-[100px] rounded-[3px]"
                items={(() => {
                  switch (record.status) {
                    case InvoiceStatus.DRAFT:
                      return [
                        {
                          label: (
                            <div
                              onClick={() => {
                                onUpdateInvoiceStatus(
                                  record,
                                  InvoiceStatus.VOID,
                                );
                              }}
                              aria-hidden="true"
                            >
                              Void
                            </div>
                          ),
                          key: InvoiceStatus.VOID,
                        },
                        /* {
                          label: (
                            <div
                              onClick={() => {
                                onUpdateInvoiceStatus(
                                  record,
                                  InvoiceStatus.INVOICE_SENT,
                                );
                              }}
                              aria-hidden="true"
                            >
                              Invoice Sent
                            </div>
                          ),
                          key: InvoiceStatus.INVOICE_SENT,
                        }, */
                      ];

                    case InvoiceStatus.INVOICE_SENT:
                      return [
                        {
                          label: (
                            <div
                              onClick={() => {
                                onUpdateInvoiceStatus(
                                  record,
                                  InvoiceStatus.APPROVED,
                                );
                              }}
                              aria-hidden="true"
                            >
                              Approved
                            </div>
                          ),
                          key: InvoiceStatus.APPROVED,
                        },
                        {
                          label: (
                            <div
                              onClick={() => {
                                onUpdateInvoiceStatus(
                                  record,
                                  InvoiceStatus.VOID,
                                );
                              }}
                              aria-hidden="true"
                            >
                              Void
                            </div>
                          ),
                          key: InvoiceStatus.VOID,
                        },
                      ];

                    default:
                      return [];
                  }
                })()}
              />
            }
            trigger={["click"]}
          >
            <button type="button">{icons.DotsVerticle()}</button>
          </Dropdown>
        </div>
      ),
  };

  return (
    <div>
      {!isClientDetails && (
        <div className="flex justify-between items-center mb-5">
          <div className="text-heading20">Invoices</div>
        </div>
      )}
      <div className="mb-4 flex justify-between">
        <div className="space-x-4">
          <SearchComponent handleSearch={handleSearch} />
          <RangePicker className="w-[256px]" onChange={onRangeChange} />
          {!isClientDetails && isClapAdminORClapManager(userDetails) && (
            <Select
              placeholder="Client"
              className="w-[190px]"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
              showSearch
              allowClear
              onChange={onClientChange}
            >
              {allClients?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          )}
          <Select
            placeholder="Status"
            className="w-[190px]"
            showSearch
            allowClear
            onChange={onStatusChange}
          >
            {Object.values(InvoiceStatus).map((item) => (
              <Option key={item} value={item}>
                {globalUtils.snakeCaseToCapitalize(item)}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Currency"
            className="w-[190px]"
            showSearch
            allowClear
            onChange={onCurrencyChange}
          >
            {currencies.map((item: Currency) => (
              <Select.Option key={item.id} value={item.name}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </div>
        {/* <div className="flex gap-x-1">
          <Button disabled>Batch Edit</Button>
          <Button
            icon={icons.DotsHorizontal()}
            className="flex items-center justify-center"
            disabled
          />
        </div> */}
      </div>
      <div>
        <Table
          // rowSelection={invoices.length && rowSelection}
          rowClassName="cursor-pointer"
          rowKey={(record: any) => {
            return record.id;
          }}
          onRow={(record) => {
            return {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onClick: (event) => {
                if (location.pathname.includes("clients"))
                  navigate(`${ROUTES.PATHS.ADMIN.INVOICES}/${record.id}`);
                else navigate(record.id);
              },
            };
          }}
          columns={tableUtils.buildColumns(invoices, columns, actions)}
          dataSource={invoices}
          pagination={false}
          size="middle"
          scroll={{ x: 1024 }}
          loading={{
            spinning: invoicesPending as boolean,
            indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
          }}
        />
      </div>
      <div className="text-right mt-4">
        <Pagination
          current={page}
          onChange={handlePageChange}
          total={invoicesResponse?.count}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
}

export default ClapInvoices;
